import React, { useEffect, useState } from "react";
import { X } from "lucide-react";

const STORAGE_KEY = "allinstra.cookie.v1";

const defaultPrefs = {
  essential: true,
  analytics: false,
  preferences: false,
};

const readPrefs = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

const persist = (prefs) => {
  try {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ ...prefs, savedAt: new Date().toISOString() })
    );
  } catch {
    /* ignore */
  }
};

const CookieBanner = () => {
  const [visible, setVisible] = useState(false);
  const [advanced, setAdvanced] = useState(false);
  const [prefs, setPrefs] = useState(defaultPrefs);

  useEffect(() => {
    const existing = readPrefs();
    if (!existing) setVisible(true);
    else setPrefs({ ...defaultPrefs, ...existing });

    const onOpen = () => {
      setAdvanced(true);
      setVisible(true);
    };
    window.addEventListener("allinstra:open-cookie-settings", onOpen);
    return () => window.removeEventListener("allinstra:open-cookie-settings", onOpen);
  }, []);

  const acceptAll = () => {
    const next = { essential: true, analytics: true, preferences: true };
    persist(next);
    setPrefs(next);
    setVisible(false);
  };

  const rejectNonEssential = () => {
    const next = { essential: true, analytics: false, preferences: false };
    persist(next);
    setPrefs(next);
    setVisible(false);
  };

  const savePrefs = () => {
    persist(prefs);
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      data-testid="cookie-banner"
      className="fixed bottom-4 left-4 right-4 md:bottom-6 md:left-6 md:right-auto md:max-w-xl z-[60] bg-ink-900 text-ink-100 border border-ink-700 shadow-2xl"
      role="dialog"
      aria-label="Cookie consent"
    >
      <div className="p-5 md:p-6">
        <div className="flex items-start justify-between gap-4">
          <div className="tag-overline" style={{ color: "#67E8F9" }}>
            Cookie Notice
          </div>
          <button
            data-testid="cookie-close"
            onClick={() => setVisible(false)}
            className="text-ink-300 hover:text-white"
            aria-label="Dismiss cookie banner"
          >
            <X size={16} />
          </button>
        </div>
        <p className="mt-3 text-sm leading-relaxed text-ink-300">
          We use cookies to improve site functionality, analyse traffic, and understand how
          visitors use Allinstra. You can accept all cookies, reject non-essential cookies, or
          manage your preferences.
        </p>

        {advanced && (
          <div className="mt-5 space-y-3 border border-ink-700 p-4 bg-ink-800/40">
            <PrefRow
              label="Essential"
              description="Required for core functionality. Always on."
              checked
              disabled
              testId="cookie-pref-essential"
              onChange={() => {}}
            />
            <PrefRow
              label="Analytics"
              description="Aggregated traffic and usage analytics."
              checked={prefs.analytics}
              testId="cookie-pref-analytics"
              onChange={(v) => setPrefs((p) => ({ ...p, analytics: v }))}
            />
            <PrefRow
              label="Preferences"
              description="Remember language and UI choices."
              checked={prefs.preferences}
              testId="cookie-pref-preferences"
              onChange={(v) => setPrefs((p) => ({ ...p, preferences: v }))}
            />
          </div>
        )}

        <div className="mt-5 flex flex-wrap gap-2">
          <button
            data-testid="cookie-accept-all"
            onClick={acceptAll}
            className="px-4 py-2.5 text-sm font-medium bg-cyan-600 text-white hover:bg-cyan-500 transition-colors"
          >
            Accept All
          </button>
          <button
            data-testid="cookie-reject"
            onClick={rejectNonEssential}
            className="px-4 py-2.5 text-sm font-medium bg-ink-800 text-white border border-ink-700 hover:bg-ink-700 transition-colors"
          >
            Reject Non-Essential
          </button>
          {!advanced ? (
            <button
              data-testid="cookie-manage"
              onClick={() => setAdvanced(true)}
              className="px-4 py-2.5 text-sm font-medium text-ink-200 hover:text-white border border-transparent hover:border-ink-700 transition-colors"
            >
              Manage Preferences
            </button>
          ) : (
            <button
              data-testid="cookie-save"
              onClick={savePrefs}
              className="px-4 py-2.5 text-sm font-medium text-ink-900 bg-white hover:bg-cyan-100 transition-colors"
            >
              Save Preferences
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

const PrefRow = ({ label, description, checked, onChange, disabled, testId }) => (
  <label
    className={`flex items-start gap-3 ${disabled ? "opacity-70 cursor-not-allowed" : "cursor-pointer"}`}
  >
    <input
      type="checkbox"
      checked={checked}
      disabled={disabled}
      data-testid={testId}
      onChange={(e) => onChange(e.target.checked)}
      className="mt-1 h-4 w-4 accent-cyan-500"
    />
    <div>
      <div className="text-sm font-medium text-white">{label}</div>
      <div className="text-xs text-ink-400">{description}</div>
    </div>
  </label>
);

export default CookieBanner;
